import React from "react";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";
import { Grid } from "@mui/material";
import Box from "@mui/material/Box";
import styles from "./AppleCountLegend.module.css";
import type { YieldBucket } from "./apple-count-helper";
import {
  NUMBER_OF_COLORS,
  bucketRange,
  colorVarForBucket,
} from "./apple-count-colors";

export type AppleCountLegendProps = {
  maxDensity: string;
  minDensity: string;
  isCalibrated?: boolean;
  calibrationDate?: string | null;
  buckets?: YieldBucket[];
};

function gradient(stops: string[]): string {
  return `linear-gradient(to right, ${stops.join(", ")})`;
}

function stop(colorVar: string, position: number): string {
  return `var(${colorVar}) ${position.toFixed(1)}%`;
}

function evenGradient(): string {
  return gradient(
    [...Array(NUMBER_OF_COLORS).keys()].map((index) =>
      stop(
        colorVarForBucket(index, NUMBER_OF_COLORS),
        (index / (NUMBER_OF_COLORS - 1)) * 100,
      ),
    ),
  );
}

function bucketGradient(buckets: YieldBucket[]): string {
  if (buckets.length < 2) return evenGradient();

  const { min, max } = bucketRange(buckets);
  const span = max - min;
  if (span <= 0) return evenGradient();

  return gradient(
    buckets.map((bucket, index) =>
      stop(
        colorVarForBucket(index, buckets.length),
        ((bucket.min - min) / span) * 100,
      ),
    ),
  );
}

export default function AppleCountLegend({
  maxDensity,
  minDensity,
  isCalibrated,
  calibrationDate,
  buckets = [],
}: AppleCountLegendProps): React.ReactNode {
  const { t, i18n } = useTranslation();
  const background = bucketGradient(buckets);

  let calibrationStatus: string;
  if (!isCalibrated) {
    calibrationStatus = t("label.uncalibrated_map");
  } else if (calibrationDate) {
    calibrationStatus = t("label.calibrated_from_manual_counts_on", {
      date: DateTime.fromISO(calibrationDate)
        .setLocale(i18n.language)
        .toLocaleString(DateTime.DATETIME_MED),
    });
  } else {
    calibrationStatus = t("label.calibrated_from_manual_counts");
  }

  return (
    <aside className={`${styles.appleLegend} boxed`}>
      <h1 className={styles.heading}>
        {isCalibrated ? t("heading.Apple Count") : t("heading.Apple Density")}
      </h1>

      <Box
        className={styles.gradientBox}
        data-testid="legend-gradient"
        style={{ background }}
      ></Box>

      <Grid container justifyContent="center">
        <Grid size={{ xs: 6 }}>
          <p data-testid="legend-min"> {minDensity}</p>
        </Grid>
        <Grid size={{ xs: 6 }}>
          <p data-testid="legend-max" style={{ textAlign: "right" }}>
            {maxDensity}
          </p>
        </Grid>
      </Grid>

      <p className={styles.calibrationStatus}>{calibrationStatus}</p>
    </aside>
  );
}
